import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useInitialSetupStatus, useLogin } from '@/hooks/useAuth';
import InitialAdminSetupModal from '@/components/InitialAdminSetupModal';
import PasswordInput from '@/components/PasswordInput';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [setupModalOpen, setSetupModalOpen] = useState(false);
  const login = useLogin();
  const navigate = useNavigate();
  const location = useLocation();

  // The bootstrap query runs once whenever the login page mounts. If the
  // backend reports no staff user exists, we auto-open the modal. Once a
  // user is created (either here or racing against another operator), the
  // query is invalidated and the modal never re-opens.
  const setupStatus = useInitialSetupStatus();
  useEffect(() => {
    if (setupStatus.data?.needsSetup) {
      setSetupModalOpen(true);
    } else {
      setSetupModalOpen(false);
    }
  }, [setupStatus.data?.needsSetup]);


  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login.mutate(
      { email, password },
      {
        onSuccess: () => navigate(from, { replace: true }),
      }
    );
  };

  return (
    <>
      <Helmet>
        <title>Entrar | DIGGER</title>
      </Helmet>
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h1 className="text-center text-3xl font-bold text-foreground">
              DIGGER
            </h1>
            <h2 className="mt-6 text-center text-2xl font-semibold text-foreground">
              Ora bem, quem é que temos aqui? 👀
            </h2>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit} aria-busy={login.isPending}>
            <div role="alert" aria-live="assertive" aria-atomic="true">
              {login.isError && (
                <div id="login-error" className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
                  {login.error.message}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="label">
                  O teu email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="input mt-1"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={login.isError || undefined}
                  aria-describedby={login.isError ? 'login-error' : undefined}
                />
              </div>

              <div>
                <div className="flex justify-between">
                  <label htmlFor="password" className="label">
                    A tua senha secreta 🔐
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-primary hover:text-primary/80"
                  >
                    Memória de peixe? 🐟
                  </Link>
                </div>
                <PasswordInput
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={login.isError || undefined}
                  aria-describedby={login.isError ? 'login-error' : undefined}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={login.isPending}
              className="btn-primary w-full"
            >
              {login.isPending ? 'Só um segundo... 🤞' : 'Vamos lá! 🚀'}
            </button>

            <p className="text-center text-sm text-muted-foreground">
              {"Ainda não és do clube? "}
              <Link to="/register" className="text-primary hover:text-primary/80 font-medium">
                Junta-te!
              </Link>
            </p>
          </form>
        </div>
      </div>

      <InitialAdminSetupModal
        isOpen={setupModalOpen}
        onClose={() => {
          setSetupModalOpen(false);
          // Re-check in case the server state changed while the modal was open.
          setupStatus.refetch();
        }}
        onSuccess={() => {
          setSetupModalOpen(false);
          setupStatus.refetch();
        }}
      />
    </>
  );
}

